import React, { useMemo, useState, useEffect } from 'react';
import { Empty, Input, Modal, Spin, Tooltip, message } from 'antd';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import { useMutation, useQueryClient } from 'react-query';

import useHouseCameras from '../../../../../../queries/useHouseCameras';
import { getQueryKeys } from '../../../../../../queries/queryKeys';
import changeCameraName from '../../../../../../requests/changeCameraName';
import { generateAddress } from '../../../../../../utils/generateAddress';
import Camera from '../../../../../../components/common/Camera/Camera';
import { cameraParentViews } from '../../../../../../components/common/Camera/constants';

import styles from './CamerasView.module.scss';

const CamerasView = ({ className }) => {
  const currentHouse = useSelector(
    (state) => state.housingStockStore.currentHouse
  );
  const canChangeCameraName = useSelector((state) =>
    Boolean(state.authentication.userInfo?.CameraChangeName)
  );

  const queryClient = useQueryClient();

  const address = useMemo(() => generateAddress(currentHouse), [currentHouse]);

  const [uniqueCameras, setUniqueCameras] = useState({});
  const [currentCamera, setCurrentCamera] = useState(null);
  const [cameraToEdit, setCameraToEdit] = useState(null);
  const [cameraName, setCameraName] = useState('');

  const houseId =
    typeof currentHouse?.houseId === 'string'
      ? Number(currentHouse.houseId)
      : currentHouse?.houseId;

  const { data: cameras, isFetching: isCamerasFetching } = useHouseCameras(
    { houseId, address },
    {
      enabled: Boolean(houseId && address),
      staleTime: Infinity,
    }
  );

  const changeCameraNameMutation = useMutation(changeCameraName, {
    onSuccess: (data, { cameraId, cameraName }) => {
      if (data === null) return;

      queryClient.setQueryData(getQueryKeys.houseCameras(houseId), (oldData) =>
        (oldData || []).map((camera) =>
          camera.id === cameraId ? { ...camera, name: cameraName } : camera
        )
      );

      setCurrentCamera((camera) =>
        camera?.id === cameraId ? { ...camera, name: cameraName } : camera
      );
      setCameraToEdit(null);
      setCameraName('');
      message.success('Наименование камеры изменено');
    },
  });

  const trimmedCameraName = cameraName.trim();
  const isCameraNameChanged =
    Boolean(trimmedCameraName) && trimmedCameraName !== cameraToEdit?.name;

  const handleSelectMainCamera = (cameraId) => {
    let foundCamera = null;

    Object.values(uniqueCameras).some((cameras) => {
      foundCamera = cameras.find((camera) => camera.id === cameraId);
      return Boolean(foundCamera);
    });

    if (foundCamera) {
      setCurrentCamera(foundCamera);
    }
  };

  const handleOpenEditModal = (camera) => {
    setCameraToEdit(camera);
    setCameraName(camera.name || '');
  };

  const handleCloseEditModal = () => {
    if (changeCameraNameMutation.isLoading) return;

    setCameraToEdit(null);
    setCameraName('');
  };

  const handleChangeCameraName = () => {
    if (
      !cameraToEdit ||
      !isCameraNameChanged ||
      changeCameraNameMutation.isLoading
    ) {
      return;
    }

    changeCameraNameMutation.mutate({
      cameraId: cameraToEdit.id,
      cameraName: trimmedCameraName,
    });
  };

  useEffect(() => {
    let groupedCameras = {};

    if (Boolean(cameras?.length)) {
      groupedCameras = cameras.reduce((acc, camera) => {
        const locationName = camera?.location?.name || 'Неизвестная локация';

        if (!acc[locationName]) {
          acc[locationName] = [];
        }
        if (!acc[locationName].find((item) => item.id === camera.id)) {
          acc[locationName].push(camera);
        }

        return acc;
      }, {});
    }

    setUniqueCameras(groupedCameras);
  }, [cameras]);

  // устанавливаем значение по умолчанию текущей камере
  useEffect(() => {
    if (!currentCamera && Object.keys(uniqueCameras).length > 0) {
      const firstLocation = Object.keys(uniqueCameras)[0];
      if (uniqueCameras[firstLocation]?.length > 0) {
        setCurrentCamera(uniqueCameras[firstLocation][0]);
      }
    }
  }, [uniqueCameras, currentCamera]);

  return (
    <Spin spinning={isCamerasFetching}>
      {Boolean(uniqueCameras) && Boolean(Object.keys(uniqueCameras).length) ? (
        <div className={cn(styles.root, className)}>
          <div className={styles.singleVideoContainer}>
            {currentCamera ? (
              <Camera
                key={currentCamera?.id}
                data={currentCamera}
                videoContainerId={currentCamera.id}
                mainScreen
                parentView={cameraParentViews.grid}
                onEditName={
                  canChangeCameraName ? handleOpenEditModal : undefined
                }
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </div>
          <div className={styles.flexVideoContainerList}>
            {Object.entries(uniqueCameras).map(([location, cameras]) => (
              <div
                className={cn(styles.flexVideoContainerItem)}
                key={location}
              >
                {cameras.map((camera, index) => (
                  <Tooltip
                    title="Вывести на большой экран"
                    placement="top"
                    key={camera.id}
                  >
                    <div className={styles.cameraVideo}>
                      {index === 0 && <h3>{location}</h3>}
                      <Camera
                        parentView={cameraParentViews.grid}
                        data={camera}
                        onSelectMainScreen={() =>
                          handleSelectMainCamera(camera.id)
                        }
                        onEditName={
                          canChangeCameraName ? handleOpenEditModal : undefined
                        }
                        videoContainerId={`${camera.id}-${index}`}
                      />
                    </div>
                  </Tooltip>
                ))}
              </div>
            ))}
          </div>
          <Modal
            visible={Boolean(cameraToEdit)}
            title="Изменение наименования камеры"
            okText="Изменить"
            cancelText="Отменить"
            confirmLoading={changeCameraNameMutation.isLoading}
            okButtonProps={{ disabled: !isCameraNameChanged }}
            maskClosable={!changeCameraNameMutation.isLoading}
            onOk={handleChangeCameraName}
            onCancel={handleCloseEditModal}
            afterClose={() => setCameraName('')}
          >
            <label
              className={styles.cameraNameLabel}
              htmlFor="camera-name"
            >
              Наименование камеры
            </label>
            <Input
              id="camera-name"
              value={cameraName}
              autoFocus
              onChange={(event) => setCameraName(event.target.value)}
              onPressEnter={handleChangeCameraName}
            />
          </Modal>
        </div>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </Spin>
  );
};

export default CamerasView;
